import { StatusIndicator } from '@neo/primitives/status-indicator'
import { humanize } from 'inflection'
import { capitalize } from 'lodash'
import type { FC } from 'react'
import type { NodeEvaluation } from '../../types'
import type { NodeExecutedEvent } from '../types'

interface ConversationQualityProps {
  nodeEvaluation: NodeEvaluation
  nextNode?: NodeExecutedEvent
}

interface QualityMetricProps {
  label: string
  status: string
  description: string
  isSuccess: boolean
}

const QualityMetric: FC<QualityMetricProps> = ({ label, status, description, isSuccess }) => {
  return (
    <div className="grid grid-cols-[100px_minmax(0,1fr)] gap-4 py-2 font-inter text-xs-400 border-b border-border last:border-b-0">
      <div className="text-secondary">
        <p>{label}</p>
      </div>
      <div className="min-w-0 break-words [overflow-wrap:anywhere]">
        <StatusIndicator tone={isSuccess ? 'success' : 'danger'} className="whitespace-normal">
          {humanize(status)}
        </StatusIndicator>
        <p className="mt-1 text-secondary leading-5">{humanize(description)}</p>
      </div>
    </div>
  )
}

export const ConversationQuality: FC<ConversationQualityProps> = ({ nodeEvaluation, nextNode }) => {
  const { hallucination, instructions_adherence, intent_identification, node_loop } = nodeEvaluation
  const chosen_path = nextNode?.chosen_path

  const metrics: QualityMetricProps[] = [
    {
      label: 'Instruction Following',
      status: instructions_adherence.adherence_passed ? 'True' : 'False',
      description: instructions_adherence.reason || 'All required instructions satisfied',
      isSuccess: instructions_adherence.adherence_passed,
    },
    {
      label: 'Loop Detection',
      status: node_loop.loop_detected ? 'Loop detected' : 'No loop detected',
      description: node_loop.reason || 'Conversation progressed linearly',
      isSuccess: !node_loop.loop_detected,
    },
    {
      label: 'Hallucination Analysis',
      status: hallucination.hallucinated ? 'Detected' : 'Not Detected',
      description: hallucination.reason || 'Agent responses matched Knowledge Base context',
      isSuccess: !hallucination.hallucinated,
    },
    {
      label: 'Intent Detection',
      status: chosen_path ? capitalize(chosen_path) : 'Not Detected',
      description: intent_identification.reason || 'Detected from dialogue cues',
      isSuccess:
        !intent_identification.intent_not_found && !intent_identification.intent_wrongly_identified,
    },
  ]

  return (
    <div className="w-full" data-testid="conversation-quality">
      <h3 className="text-h4-500 mb-3">Conversation Quality</h3>
      <div className="flex flex-col">
        {metrics.map((metric) => (
          <QualityMetric
            key={metric.label}
            label={metric.label}
            status={metric.status}
            description={metric.description}
            isSuccess={metric.isSuccess}
          />
        ))}
      </div>
    </div>
  )
}
